// Card.jsx
import React, { useEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { ParseNumber } from "../../../utils/ParseNumber.js";

function useInViewport(ref, options = { threshold: 0.35 }) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      options
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, options.threshold]);
  return inView;
}

function Card({ statistic }) {
  const wrapperRef = useRef(null);
  const inView = useInViewport(wrapperRef, { threshold: 0.35 });
  const startRef = useRef(null); // will hold CountUp's start() function

  // parse the numeric value (safe fallback to 0)
  const target = Number(ParseNumber(statistic?.count ?? 0)) || 0;

  useEffect(() => {
    if (inView && typeof startRef.current === "function") {
      startRef.current(); // trigger count animation when card enters viewport
    }
  }, [inView]);

  return (
    <div
      ref={wrapperRef}
      className=" w-[252px] md:w-[182px] h-[170px] mx-h-[279px] bg-gray-100 cursor-pointer border shadow-sm rounded-lg flex flex-col items-center text-center transform transition duration-300 hover:scale-105"
    >
      {/* Icon positioned at the top center */}
      <div className="relative w-full">
        <img
          src={process.env.REACT_APP_IMG_URL + statistic.icon}
          alt={statistic.name}
          className="w-16 h-16 object-cover background p-2 rounded-md border absolute -top-10 left-1/2 transform -translate-x-1/2"
        />
      </div>

      {/* Statistic content */}
      <div className="overflow-hidden mt-10">
        <h3 className="text-3xl font-bold text-gray-800">
          <CountUp start={0} end={target} duration={2} preserveValue={true}>
            {({ countUpRef, start }) => {
              // store start so we can call it on intersection
              startRef.current = start;
              // show 0 until started
              return <span ref={countUpRef}>0</span>;
            }}
          </CountUp>
        </h3>
        <p className="text-gray-900 text-xl mt-2 overflow-hidden">{statistic.name}</p>
      </div>
    </div>
  );
}

export default Card;
